"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Wifi, WifiLow, WifiOff, AlertCircle } from "lucide-react";
import DailyIframe from "@daily-co/daily-js";
import { Button } from "~/components/ui/button";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { destroyDailyCallObject } from "~/components/chat/components/dailyjs/daily-call-object-lifecycle";
import { cn } from "~/lib/utils";
import {
  diagnosticErrorName,
  recordDeviceTestEvent,
  type AttemptReason,
  type CompletionMethod,
  type DiagnosticPhase,
} from "./diagnostics";

interface NetworkCheckProps {
  onPass: () => void;
}

type TestState = "idle" | "testing" | "good" | "warning" | "bad" | "error";

const TEST_DURATION_MS = 15000;

export function NetworkCheck({ onPass }: NetworkCheckProps) {
  const [state, setState] = useState<TestState>("idle");
  const [progress, setProgress] = useState(0);
  const dailyRef = useRef<ReturnType<
    typeof DailyIframe.createCallObject
  > | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoPassTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  const runGenerationRef = useRef(0);
  const cleanupPromiseRef = useRef<Promise<boolean> | null>(null);

  const cleanup = useCallback(
    async (
      phase:
        | "network-test-failed"
        | "network-test-restarted"
        | "network-test-unmounted",
    ) => {
      if (progressRef.current) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      if (autoPassTimerRef.current) {
        clearTimeout(autoPassTimerRef.current);
        autoPassTimerRef.current = null;
      }

      if (cleanupPromiseRef.current) {
        return cleanupPromiseRef.current;
      }

      const daily = dailyRef.current;
      dailyRef.current = null;
      if (!daily) {
        return true;
      }

      const cleanupPromise = destroyDailyCallObject(
        daily,
        { pathname: "/device-test" },
        phase,
      );
      cleanupPromiseRef.current = cleanupPromise;

      try {
        return await cleanupPromise;
      } finally {
        if (cleanupPromiseRef.current === cleanupPromise) {
          cleanupPromiseRef.current = null;
        }
      }
    },
    [],
  );

  const complete = useCallback(
    (method: CompletionMethod) => {
      recordDeviceTestEvent("step_completed", {
        step: "network",
        attempt: runGenerationRef.current,
        method,
      });
      recordDeviceTestEvent("completed", { method });
      onPass();
    },
    [onPass],
  );

  const runTest = useCallback(
    async (reason: AttemptReason = "initial") => {
      const runGeneration = runGenerationRef.current + 1;
      runGenerationRef.current = runGeneration;
      recordDeviceTestEvent("started", {
        step: "network",
        attempt: runGeneration,
        reason,
      });
      const cleanedUp = await cleanup("network-test-restarted");
      if (runGeneration !== runGenerationRef.current) {
        return;
      }

      if (!cleanedUp) {
        if (mountedRef.current) {
          recordDeviceTestEvent("failed", {
            step: "network",
            attempt: runGeneration,
            phase: "network_cleanup",
            error_name: "unknown",
          });
          setState("error");
        }
        return;
      }

      if (!mountedRef.current) {
        return;
      }

      setState("testing");
      setProgress(0);

      let daily: ReturnType<typeof DailyIframe.createCallObject> | null = null;
      let phase: DiagnosticPhase = "network_init";

      try {
        const createdDaily = DailyIframe.createCallObject({
          subscribeToTracksAutomatically: false,
          dailyConfig: { avoidEval: true },
        });
        daily = createdDaily;
        dailyRef.current = createdDaily;

        phase = "network_pre_auth";
        await createdDaily.preAuth({
          url: "https://maindcrafter.daily.co/_demo",
        });
        if (!mountedRef.current || runGeneration !== runGenerationRef.current)
          return;

        const startTime = Date.now();
        progressRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          setProgress(Math.min((elapsed / TEST_DURATION_MS) * 100, 95));
        }, 200);

        stopTimerRef.current = setTimeout(
          () => createdDaily.stopTestCallQuality(),
          TEST_DURATION_MS,
        );

        phase = "network_quality";
        const results = await createdDaily.testCallQuality();

        if (
          !mountedRef.current ||
          runGeneration !== runGenerationRef.current ||
          dailyRef.current !== createdDaily
        ) {
          return;
        }

        if (stopTimerRef.current) {
          clearTimeout(stopTimerRef.current);
          stopTimerRef.current = null;
        }
        if (progressRef.current) {
          clearInterval(progressRef.current);
          progressRef.current = null;
        }
        setProgress(100);
        recordDeviceTestEvent("result", {
          step: "network",
          attempt: runGeneration,
          result: results.result,
        });

        if (results.result === "failed" || results.result === "aborted") {
          setState("error");
        } else {
          setState(results.result);
          if (results.result === "good" || results.result === "warning") {
            // Auto-pass after a brief moment so user sees the result
            autoPassTimerRef.current = setTimeout(
              () => complete("automatic"),
              1200,
            );
          }
        }
      } catch (err) {
        if (daily && dailyRef.current === daily) {
          await cleanup("network-test-failed");
        }
        if (mountedRef.current && runGeneration === runGenerationRef.current) {
          recordDeviceTestEvent("failed", {
            step: "network",
            attempt: runGeneration,
            phase,
            error_name: diagnosticErrorName(err),
          });
          setState("error");
        }
      }
    },
    [cleanup, complete],
  );

  useEffect(() => {
    mountedRef.current = true;
    void runTest();

    return () => {
      mountedRef.current = false;
      runGenerationRef.current += 1;
      void cleanup("network-test-unmounted");
    };
  }, [cleanup, runTest]);

  return (
    <div className="space-y-4">
      {state === "testing" && (
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            Testiramo kvalitet konekcije...
          </p>
          <div className="bg-muted h-2 overflow-hidden rounded-full">
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-200 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {state === "good" && (
        <div
          className={cn(
            "flex items-center gap-3 rounded-xl p-4",
            "bg-green-50 text-green-700",
          )}
        >
          <Wifi className="h-5 w-5" />
          <div>
            <p className="font-medium">Stabilna konekcija</p>
            <p className="text-sm opacity-80">
              Vaša internet veza je dobra za video poziv.
            </p>
          </div>
        </div>
      )}

      {state === "warning" && (
        <div
          className={cn(
            "flex items-center gap-3 rounded-xl p-4",
            "bg-yellow-50 text-yellow-700",
          )}
        >
          <WifiLow className="h-5 w-5" />
          <div>
            <p className="font-medium">Slabija konekcija</p>
            <p className="text-sm opacity-80">
              Video poziv bi trebalo da radi, ali moguća su povremena usporenja.
            </p>
          </div>
        </div>
      )}

      {state === "bad" && (
        <div className="space-y-3">
          <div
            className={cn(
              "flex items-center gap-3 rounded-xl p-4",
              "bg-red-50 text-red-700",
            )}
          >
            <WifiOff className="h-5 w-5" />
            <div>
              <p className="font-medium">Loša konekcija</p>
              <p className="text-sm opacity-80">
                Vaša internet veza može praviti probleme tokom video poziva.
                Pokušajte da se približite ruteru ili koristite žičanu
                konekciju.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => complete("continue_anyway")}
            className="w-full"
          >
            Nastavi svejedno
          </Button>
        </div>
      )}

      {state === "error" && (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Nismo uspeli da testiramo konekciju.
            </AlertDescription>
          </Alert>
          <ul className="text-muted-foreground list-inside list-disc space-y-1.5 text-xs">
            <li>Proverite da li ste povezani na internet</li>
            <li>Ako koristite VPN, pokušajte da ga isključite</li>
            <li>
              Pokušajte da se prebacite sa Wi-Fi na mobilne podatke (ili
              obrnuto)
            </li>
          </ul>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void runTest("retry")}
              className="flex-1"
            >
              Pokušaj ponovo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => complete("skipped")}
              className="flex-1"
            >
              Preskoči
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
